import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "yu_pwa_dismissed";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

// Floating, dismissible "install app" banner. Uses the native beforeinstallprompt
// on Android/desktop Chrome, and shows Safari "Add to Home" instructions on iOS.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari has no beforeinstallprompt — show a hint after a short delay.
    let t;
    if (isIos()) {
      t = setTimeout(() => setVisible(true), 1800);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
      dismiss();
    } else if (isIos()) {
      setIosHint(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md rounded-2xl p-4 shadow-2xl"
      style={{ background: "#1C1815", border: "1px solid #2A241F" }}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center" style={{ background: "#12100E", border: "1px solid #2A241F" }}>
          <img src="/icon-192.png" alt="YU YieldUp" className="h-8 w-8 rounded-lg" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold" style={{ color: "#FAF7F2" }}>Installa YU YieldUp</p>
          {iosHint ? (
            <p className="mt-1 text-xs leading-relaxed" style={{ color: "#C4BBB0" }}>
              Tocca <Share className="inline h-3.5 w-3.5" style={{ color: "#E05A2C" }} /> Condividi in Safari, poi
              <strong style={{ color: "#FAF7F2" }}> “Aggiungi a Home”</strong>.
            </p>
          ) : (
            <p className="mt-1 text-xs" style={{ color: "#C4BBB0" }}>Aggiungila alla schermata Home e aprila come un’app.</p>
          )}
        </div>
        <button onClick={dismiss} aria-label="Chiudi" className="shrink-0" style={{ color: "#857C72" }}>
          <X className="h-4 w-4" />
        </button>
      </div>
      {!iosHint ? (
        <button onClick={install}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#F26B3A]"
          style={{ background: "#E05A2C" }}>
          <Download className="h-4 w-4" /> {isIos() ? "Come si installa" : "Installa l’app"}
        </button>
      ) : null}
    </div>
  );
}
